// Thin wrappers over the Part 2 room-recovery RPCs (contract §4).
//
// Every wrapper resolves to { data, error, copy } and never throws:
//   data   the RPC's JSON on success (null on error)
//   error  { code, message } or null
//   copy   the sentence to show the player when something went wrong
//
// Argument names are exactly the contract's, p_ prefix included.
import { supabase } from '@tasted/utils/supabase';

export const NETWORK_COPY = "Couldn't reach the game — check your connection and try again.";
export const FEATURE_OFF_COPY = "This isn't switched on for this game.";

// Per-code fallbacks. Part 2 raises every P04xx with the user-facing sentence
// as its message (names already substituted), so those are shown as sent and
// these only cover a missing message.
const FALLBACK = {
  '42501': 'Only the game master can do that.',
  P0403: 'That only works while the game is running.',
  P0404: "We couldn't find that in this game.",
  P0409: 'A vote is already running.',
  P0410: 'That has already finished.',
  P0412: "That can't be done right now.",
  P0423: 'None left for this game.',
  P0429: 'Too many tries — wait a minute.',
  P0501: FEATURE_OFF_COPY,
  '22023': 'Something went wrong on our side. Try again, or message us.',
};

// Codes whose message is written for players (contract §1).
const USER_FACING = new Set(['P0404', 'P0409', 'P0410', 'P0412', 'P0423', 'P0429']);

// Per-RPC overrides where one code means something specific.
const BY_RPC = {
  advance_game_step: {
    '42501': 'Only the game master can move the game on.',
    '22023': 'This is already the last screen.',
    P0403: 'The game has not started yet.',
  },
  restart_question: {
    P0410: 'The host restarted this question — answer again.',
    P0423: 'No restarts left for this game.',
  },
  restart_session_run: { P0423: 'No resets left for this game.' },
  cast_session_vote: { '42501': "You can't vote on this one.", P0410: 'That vote has finished.' },
  resolve_session_vote: { P0410: 'That vote has finished.' },
  cancel_session_vote: { '42501': 'Only the person who started the vote can cancel it.', P0410: 'That vote has finished.' },
  open_session_vote: { '42501': "Only players who were here when the game started can start a vote." },
  transfer_gm: { '22023': "You're already the game master." },
  set_seat_active: { '22023': "You can't mark yourself away." },
  reactivate_my_seat: { P0412: 'Ask the GM to let you back in.' },
  request_readmit: { P0410: 'That request has expired.' },
  readmit_request_status: { P0410: 'Your request timed out. Ask again when the GM is ready.' },
  approve_readmit: { P0410: 'That request has expired.' },
  decline_readmit: { P0410: 'That request has already closed.' },
};

function isNetworkError(err) {
  if (!err) return false;
  if (err.name === 'TypeError' || err.name === 'AbortError') return true;
  const msg = String(err.message || '');
  return /Failed to fetch|NetworkError|Load failed|network/i.test(msg) && !err.code;
}

/**
 * The sentence to show for an RPC error. Pure; exported for tests.
 * @param {{code?:string,message?:string}|null} error
 * @param {string} [rpc]
 */
export function roomErrorCopy(error, rpc) {
  if (!error) return '';
  if (isNetworkError(error)) return NETWORK_COPY;
  const code = error.code || '';
  const msg = String(error.message || '').trim();

  // Bad claim token: the seat now belongs to another phone (a re-admit).
  if (code === '42501' && /not your player/i.test(msg)) {
    return "This phone isn't linked to that player any more.";
  }
  // A sentence written for players ends in punctuation. Internal messages
  // ('stale run', 'tap ready first') are bare fragments. Don't require a
  // capital: Part 2 sentences often start with a nickname ("sam is still
  // here — ask them to hand over.").
  const isSentence = /[.!?…]$/.test(msg);
  if (USER_FACING.has(code) && isSentence) return msg;
  const rpcCopy = BY_RPC[rpc]?.[code];
  if (rpcCopy) return rpcCopy;
  if (code === '42501' && isSentence && /^[A-Z]/.test(msg)) return msg;
  if (FALLBACK[code]) return FALLBACK[code];
  if (!code) return NETWORK_COPY;
  return FALLBACK['22023'];
}

export const isFeatureOff = (error) => error?.code === 'P0501';

/** The function does not exist yet (migrations not applied) — PostgREST 404. */
export const isMissingFunction = (error) =>
  error?.code === 'PGRST202' || error?.code === '42883' || Number(error?.status) === 404;

async function call(rpc, args) {
  try {
    const { data, error } = await supabase.rpc(rpc, args);
    if (error) {
      return { data: null, error, copy: roomErrorCopy(error, rpc) };
    }
    return { data: data ?? null, error: null, copy: '' };
  } catch (err) {
    const error = { code: err?.code || '', message: err?.message || String(err), name: err?.name };
    return { data: null, error, copy: roomErrorCopy(error, rpc) };
  }
}

// ── Flags, budget, presence ────────────────────────────────────────────────
export const getSessionFeatures = (sessionId) =>
  call('game_session_features', { p_session_id: sessionId });

export const getRecoveryBudget = (sessionId) =>
  call('room_recovery_budget', { p_session_id: sessionId });

export const touchSeat = ({ sessionId, playerId, claimToken }) =>
  call('touch_seat', { p_session_id: sessionId, p_player_id: playerId, p_claim_token: claimToken });

// ── Step authority ─────────────────────────────────────────────────────────
export const advanceGameStep = ({ sessionId, playerId, claimToken, runId, fromStep, expectedVersion, source = 'help_sheet', gmSecret = null }) =>
  call('advance_game_step', {
    p_session_id: sessionId,
    p_actor_player_id: playerId,
    p_claim_token: claimToken,
    p_run_id: runId,
    p_from_step: fromStep,
    p_expected_version: expectedVersion,
    p_source: source,
    p_gm_secret: gmSecret,
  });

// ── Restart / reset ────────────────────────────────────────────────────────
export const restartQuestion = ({ sessionId, playerId, claimToken, runId, stepIndex, roundId, expectedVersion, gmSecret = null }) =>
  call('restart_question', {
    p_session_id: sessionId,
    p_actor_player_id: playerId,
    p_claim_token: claimToken,
    p_run_id: runId,
    p_step_index: stepIndex,
    p_round_id: roundId,
    p_expected_version: expectedVersion,
    p_gm_secret: gmSecret,
  });

export const restartSessionRun = ({ sessionId, playerId, claimToken, runId, gmSecret = null }) =>
  call('restart_session_run', {
    p_session_id: sessionId,
    p_actor_player_id: playerId,
    p_claim_token: claimToken,
    p_run_id: runId,
    p_gm_secret: gmSecret,
  });

// ── Seats ──────────────────────────────────────────────────────────────────
export const setSeatActive = ({ sessionId, playerId, claimToken, targetId, active, needsApproval = false, gmSecret = null }) =>
  call('set_seat_active', {
    p_session_id: sessionId,
    p_actor_player_id: playerId,
    p_claim_token: claimToken,
    p_target_player_id: targetId,
    p_active: active,
    p_needs_approval: needsApproval,
    p_gm_secret: gmSecret,
  });

export const reactivateMySeat = ({ sessionId, playerId, claimToken }) =>
  call('reactivate_my_seat', { p_session_id: sessionId, p_player_id: playerId, p_claim_token: claimToken });

// ── Votes ──────────────────────────────────────────────────────────────────
export const openSessionVote = ({ sessionId, playerId, claimToken, kind, targetId }) =>
  call('open_session_vote', {
    p_session_id: sessionId,
    p_actor_player_id: playerId,
    p_claim_token: claimToken,
    p_kind: kind,
    p_target_player_id: targetId,
  });

export const castSessionVote = ({ sessionId, playerId, claimToken, voteId, choice }) =>
  call('cast_session_vote', {
    p_session_id: sessionId,
    p_actor_player_id: playerId,
    p_claim_token: claimToken,
    p_vote_id: voteId,
    p_choice: choice,
  });

export const resolveSessionVote = ({ sessionId, playerId, claimToken, voteId }) =>
  call('resolve_session_vote', {
    p_session_id: sessionId,
    p_actor_player_id: playerId,
    p_claim_token: claimToken,
    p_vote_id: voteId,
  });

export const cancelSessionVote = ({ sessionId, playerId, claimToken, voteId }) =>
  call('cancel_session_vote', {
    p_session_id: sessionId,
    p_actor_player_id: playerId,
    p_claim_token: claimToken,
    p_vote_id: voteId,
  });

export const getSessionVoteState = ({ sessionId, playerId, claimToken }) =>
  call('session_vote_state', { p_session_id: sessionId, p_player_id: playerId, p_claim_token: claimToken });

export const transferGm = ({ sessionId, playerId, claimToken, newGmId, gmSecret = null }) =>
  call('transfer_gm', {
    p_session_id: sessionId,
    p_actor_player_id: playerId,
    p_claim_token: claimToken,
    p_new_gm: newGmId,
    p_gm_secret: gmSecret,
  });

// ── Re-admit ───────────────────────────────────────────────────────────────
export const requestReadmit = ({ sessionId, username, requestSecret }) =>
  call('request_readmit', { p_session_id: sessionId, p_username: username, p_request_secret: requestSecret });

export const readmitRequestStatus = ({ requestId, requestSecret }) =>
  call('readmit_request_status', { p_request_id: requestId, p_request_secret: requestSecret });

export const approveReadmit = ({ sessionId, playerId, claimToken, requestId, code, gmSecret = null }) =>
  call('approve_readmit', {
    p_session_id: sessionId,
    p_actor_player_id: playerId,
    p_claim_token: claimToken,
    p_request_id: requestId,
    p_code: code,
    p_gm_secret: gmSecret,
  });

export const declineReadmit = ({ sessionId, playerId, claimToken, requestId, gmSecret = null }) =>
  call('decline_readmit', {
    p_session_id: sessionId,
    p_actor_player_id: playerId,
    p_claim_token: claimToken,
    p_request_id: requestId,
    p_gm_secret: gmSecret,
  });

export const issueReadmitCode = ({ sessionId, playerId, claimToken, targetId, gmSecret = null }) =>
  call('issue_readmit_code', {
    p_session_id: sessionId,
    p_actor_player_id: playerId,
    p_claim_token: claimToken,
    p_target_player_id: targetId,
    p_gm_secret: gmSecret,
  });

/**
 * GM-side copy for approve_readmit's returned (not raised) ok:false shapes
 * (contract §1, spec §4 copy map).
 */
export function approveResultCopy(result, name = 'them') {
  if (!result || result.ok) return '';
  if (result.reason === 'wrong_code') {
    const left = Number(result.tries_left);
    const tries = Number.isFinite(left) ? ` ${left} ${left === 1 ? 'try' : 'tries'} left.` : '';
    return `That code doesn't match — check their screen.${tries}`;
  }
  if (result.reason === 'too_many_wrong') {
    return `Too many wrong codes, so this request was closed. Ask ${name} to ask again, or give them a code.`;
  }
  return "That didn't work. Try again.";
}

/** Friendly names for step types, for "Move everyone from X to Y". */
const STEP_LABELS = {
  intro: 'the welcome',
  'participation-mode': 'team setup',
  'game-view-mode': 'screen setup',
  'wine-intro': 'the wine intro',
  question: 'the question',
  reveal: 'the answer reveal',
  'reveal-answer': 'the answer reveal',
  discussion: 'the discussion',
  results: 'the results',
  scoreboard: 'the scoreboard',
  'wine-review': 'the wine rating',
  'wine-rating-reveal': "the group's rating",
  'wine-reveal': 'the wine reveal',
  'group-review': 'the wine round-up',
  'final-leaderboard': 'the final leaderboard',
  prize: 'the prizes',
  'thank-you': 'the thank-you screen',
};

export function stepLabel(step) {
  if (!step) return 'the next screen';
  return STEP_LABELS[step.type] || 'the next screen';
}

/** Index of the first final-leaderboard step, or Infinity when there is none. */
export function finalLeaderboardIndex(flowSteps) {
  const i = (flowSteps || []).findIndex((s) => s?.type === 'final-leaderboard');
  return i === -1 ? Infinity : i;
}

/**
 * "Wine 1 · Question 5 of 7" for a question step: wine number in serving
 * order, and this question's place among that wine's question steps.
 */
export function questionPosition(flowSteps, stepIndex) {
  const steps = flowSteps || [];
  const step = steps[stepIndex];
  if (!step || step.type !== 'question') return null;
  const wines = [];
  for (const s of steps) {
    if (s?.wine && !wines.includes(s.wine)) wines.push(s.wine);
  }
  const sameWine = steps.filter((s) => s?.type === 'question' && s.wine === step.wine);
  const at = sameWine.indexOf(step);
  return {
    wineNumber: wines.indexOf(step.wine) + 1,
    questionNumber: at + 1,
    questionCount: sameWine.length,
    wineId: step.wine,
    roundNumber: Number(step.index) + 1,
  };
}
